import React, { useState, useEffect } from 'react';
import { apiFetch } from '../../utils/api';
import { uploadToCloudinary } from '../../utils/cloudinary';
import '../../styles/admin/AddProjectGroup.css';
import ProjectCategories from '../../constants/projectCategories';
import ProtectedAdminRoute from '../../components/ProtectedAdminRoute';

export default function AddProjectGroup() {
  const [groupName, setGroupName] = useState('');
  const [description, setDescription] = useState('');
  const [image, setImage] = useState('');
  const [category, setCategory] = useState('');
  const categories = [
    { value: '', label: 'Select category (optional)' },
    ...ProjectCategories.OPTIONS.map(cat => ({ value: cat, label: cat }))
  ];
  const [sections, setSections] = useState([]); // Dynamic content blocks
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState('');
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [imageUploading, setImageUploading] = useState(false);
  // const navigate = useNavigate(); // Removed as unused
  
  useEffect(() => {
    apiFetch('/api/members')
    .then(data => {
      if (Array.isArray(data)) {
        setMembers(data);
      } else if (data && Array.isArray(data.members)) {
        setMembers(data.members);
      } else {
        setMembers([]);
      }
    })
    .catch(err => {
      console.error('Failed to load members:', err);
      setMessage('Failed to load members');
    });
  }, []);
  
  useEffect(() => {
    let arr = Array.isArray(members) ? members : [];
    if (!search) {
      setFilteredMembers(arr);
    } else {
      setFilteredMembers(
        arr.filter(m => (
          (m.name && m.name.toLowerCase().includes(search.toLowerCase())) ||
          (m.email && m.email.toLowerCase().includes(search.toLowerCase()))
        ))
      );
    }
  }, [search, members]);
  
  // Section logic (copied from AddProject)
  const addSection = (type = 'paragraph') => {
    setSections([...sections, { type, content: '' }]);
  };
  const updateSection = (index, value) => {
    setSections(sections.map((s, i) => i === index ? { ...s, content: value } : s));
  };
  const updateSectionType = (index, type) => {
    setSections(sections.map((s, i) => i === index ? { ...s, type } : s));
  };
  const removeSection = (index) => {
    setSections(sections.filter((_, i) => i !== index));
  };
  
  const handleSelectMember = (member) => {
    if (!selectedMembers.some(m => m._id === member._id)) {
      setSelectedMembers([...selectedMembers, member]);
    }
  };
  
  const handleRemoveMember = (id) => {
    setSelectedMembers(selectedMembers.filter(m => m._id !== id));
  };
  
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageUploading(true);
    const previewUrl = URL.createObjectURL(file);
    setImage(previewUrl); // Show preview immediately
    try {
      const result = await uploadToCloudinary(file);
      if (result && result.success && typeof result.url === 'string' && result.url.startsWith('http')) {
        setImage(result.url); // Replace preview with Cloudinary URL only if valid
      } else {
        setImage(previewUrl); // Keep preview if upload fails
        setMessage('Image upload failed');
      }
    } catch (err) {
      setMessage('Image upload failed');
      setImage(previewUrl); // Keep preview if upload fails
    }
    setImageUploading(false);
  };
  
  const handleSectionImageUpload = async (idx, file) => {
    if (!file) return;
    setImageUploading(true);
    const previewUrl = URL.createObjectURL(file);
    setSections(sections => sections.map((s, i) => i === idx ? { ...s, content: previewUrl } : s));
    try {
      const result = await uploadToCloudinary(file);
      if (result && result.success && typeof result.url === 'string' && result.url.startsWith('http')) {
        setSections(sections => sections.map((s, i) => i === idx ? { ...s, content: result.url } : s));
        setMessage(''); // Clear any previous error
      } else {
        setMessage('Section image upload failed: Invalid Cloudinary URL');
      }
    } catch (err) {
      setMessage('Section image upload failed: ' + (err?.message || 'Unknown error'));
    }
    setImageUploading(false);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    
    // Validation: ensure all image sections have a valid Cloudinary URL
    const invalidSection = sections.find(
      s => s.type === 'image' && (!s.content || typeof s.content !== 'string' || !s.content.startsWith('http'))
    );
    if (invalidSection) {
      setMessage('Please wait for all section images to finish uploading before submitting.');
      setSaving(false);
      return;
    }
    
    try {
      const data = await apiFetch('/api/project-groups', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: groupName,
          description,
          image,
          category,
          sections,
          members: selectedMembers.map(m => m._id)
        }),
      });
      
      console.log('✅ Created project group:', data);
      setMessage('Project group added successfully!');
      setGroupName('');
      setDescription('');
      setImage('');
      setCategory('');
      setSections([]);
      setSelectedMembers([]);
    } catch (err) {
      console.error('❌ Error adding project group:', err);
      setMessage(`Error adding project group: ${err.message}`);
    }
    setSaving(false);
  };
  
  return (
    <ProtectedAdminRoute>
    <div className="admin-add-page add-project-group-page">
    <div className="admin-add-header">
    <h2>Add Project Group</h2>
    <p className="admin-add-subtitle">Create a new project group and assign members</p>
    </div>
    <form className="admin-add-form" onSubmit={handleSubmit} autoComplete="off">
    {/* ...existing code... */}
    <div className="form-group">
    <label htmlFor="groupName">Project Group Name</label>
    <input
    id="groupName"
    type="text"
    value={groupName}
    onChange={e => setGroupName(e.target.value)}
    required
    placeholder="Enter project group name"
    className="form-control"
    />
    </div>
    {/* ...existing code... */}
    <div className="form-group">
    <label htmlFor="description">Short Description</label>
    <textarea
    id="description"
    value={description}
    onChange={e => setDescription(e.target.value)}
    placeholder="Short summary of the project group"
    className="form-control"
    rows={2}
    />
    </div>
    {/* ...existing code... */}
    <div className="form-group">
    <label htmlFor="image">Group Image</label>
    <input
    id="image"
    type="file"
    accept="image/*"
    onChange={handleImageUpload}
    className="form-control"
    disabled={imageUploading}
    />
    {imageUploading && <div style={{color:'#888',marginTop:4}}>Uploading...</div>}
    {image && (
      <div style={{marginTop:8}}>
      <img src={image} alt="Group" style={{maxWidth:180, borderRadius:8}} />
      </div>
    )}
    </div>
    {/* ...existing code... */}
    <div className="form-group">
    <label htmlFor="category">Category</label>
    <select
    id="category"
    value={category}
    onChange={e => setCategory(e.target.value)}
    className="form-control"
    >
    {categories.map(opt => (
      <option key={opt.value} value={opt.value}>{opt.label}</option>
    ))}
    </select>
    </div>
    {/* ...existing code... */}
    <div className="form-group">
    <label>Content Sections</label>
    {sections.map((section, idx) => (
      <div key={idx} className="section-block">
      <select
      value={section.type}
      onChange={e => updateSectionType(idx, e.target.value)}
      className="form-control"
      >
      <option value="paragraph">Paragraph</option>
      <option value="image">Image</option>
      </select>
      {section.type === 'paragraph' ? (
        <textarea
        value={section.content}
        onChange={e => updateSection(idx, e.target.value)}
        placeholder="Paragraph text"
        className="form-control"
        rows={3}
        />
      ) : (
        <div>
        <input
        type="text"
        value={section.content}
        onChange={e => updateSection(idx, e.target.value)}
        placeholder="Image URL or upload below"
        className="form-control"
        style={{marginBottom:6}}
        />
        <input
        type="file"
        accept="image/*"
        onChange={e => handleSectionImageUpload(idx, e.target.files[0])}
        className="form-control"
        disabled={imageUploading}
        />
        {typeof section.content === 'string' && section.content.startsWith('http') && (
          <img src={section.content} alt="Section" style={{maxWidth:140, marginTop:6, borderRadius:6}} />
        )}
        </div>
      )}
      <button type="button" onClick={() => removeSection(idx)} className="action-btn secondary" style={{marginTop: 4}}>Remove</button>
      </div>
    ))}
    <button type="button" onClick={() => addSection('paragraph')} className="action-btn primary" style={{marginTop: 8, marginRight: 8}}>Add Paragraph</button>
    <button type="button" onClick={() => addSection('image')} className="action-btn secondary" style={{marginTop: 8}}>Add Image</button>
    </div>
    {/* ...existing code... */}
    <div className="form-group">
    <label htmlFor="memberSearch">Add Members</label>
    <input
    id="memberSearch"
    type="text"
    value={search}
    onChange={e => setSearch(e.target.value)}
    placeholder="Search members by name"
    className="form-control"
    />
    <div className="member-search-results">
    {filteredMembers.slice(0, 8).map(member => (
      <div
      key={member._id}
      className={`member-search-item${selectedMembers.some(m => m._id === member._id) ? ' selected' : ''}`}
      onClick={() => handleSelectMember(member)}
      style={{ cursor: 'pointer', opacity: selectedMembers.some(m => m._id === member._id) ? 0.5 : 1 }}
      >
      {member.name} {member.email && <span className="member-email">({member.email})</span>}
      </div>
    ))}
    </div>
    </div>
    {/* ...existing code... */}
    <div className="form-group selected-members">
    <label>Selected Members</label>
    <div className="selected-members-list">
    {selectedMembers.map(member => (
      <div key={member._id} className="selected-member-card">
      <span>{member.name}</span>
      <button type="button" onClick={() => handleRemoveMember(member._id)}>&times;</button>
      </div>
    ))}
    {selectedMembers.length === 0 && <span className="no-members">No members selected</span>}
    </div>
    </div>
    {/* ...existing code... */}
    <button type="submit" className="action-btn primary" disabled={saving || !groupName}>
    {saving ? 'Saving...' : 'Add Project Group'}
    </button>
    {message && (
      <div className={`message ${message.startsWith('Error') ? 'error-msg' : 'success-msg'}`}>{message}</div>
    )}
    </form>
    </div>
    </ProtectedAdminRoute>
  );
}
